/**
 * UI action dispatcher — the single place where validated UIActions become
 * store mutations and route changes. Client-only (touches the world store).
 */

import type { UIAction } from './actions';
import type { ZoneId } from '../lab/types';
import { useWorld } from '../lab/store';
import { localePath, type Lang } from '../i18n/config';

export interface RouterLike {
  push: (href: string) => void;
}

/** Logical route each zone resolves to when focused (locale added at dispatch). */
export const ZONE_ROUTE: Record<ZoneId, string> = {
  core: '/',
  research: '/research',
  projects: '/projects',
  agents: '/agents',
  knowledge: '/knowledge',
  observatory: '/observatory',
  archive: '/publications',
  gateway: '/contact',
};

/** Execute one validated action against the store and router, in `lang`. */
export function executeAction(action: UIAction, router: RouterLike, lang: Lang = 'en'): void {
  const s = useWorld.getState();
  const go = (logical: string) => router.push(localePath(lang, logical));
  switch (action.type) {
    case 'NAVIGATE':
      go(action.target);
      break;
    case 'OPEN_PROJECT':
      s.setFocus({ type: 'project', id: action.slug });
      go(`/projects/${action.slug}`);
      break;
    case 'OPEN_RESEARCH':
      s.setFocus({ type: 'research', id: action.slug });
      go(`/research/${action.slug}`);
      break;
    case 'FOCUS_ZONE':
      s.setZone(action.zone);
      go(ZONE_ROUTE[action.zone]);
      break;
    case 'FOCUS_NODE':
      s.setFocus({ type: 'node', id: action.id });
      go('/knowledge');
      break;
    case 'SHOW_GRAPH':
      s.setFocus(null);
      go('/knowledge');
      break;
    case 'SHOW_AGENT':
      s.setFocus({ type: 'agent', id: action.id });
      go('/agents');
      break;
    case 'COMPARE':
      s.setCompare({ a: action.a, b: action.b });
      break;
  }
}

/** Machine-voice chip text for an executed action. */
export function describeAction(action: UIAction): string {
  switch (action.type) {
    case 'NAVIGATE':
      return `→ opening ${action.target}`;
    case 'OPEN_PROJECT':
      return `→ opening /projects/${action.slug}`;
    case 'OPEN_RESEARCH':
      return `→ opening /research/${action.slug}`;
    case 'FOCUS_ZONE':
      return `→ focusing ${action.zone} zone`;
    case 'FOCUS_NODE':
      return `→ focusing node ${action.id}`;
    case 'SHOW_GRAPH':
      return action.filter
        ? `→ opening knowledge graph · ${action.filter}`
        : '→ opening knowledge graph';
    case 'SHOW_AGENT':
      return `→ showing agent ${action.id.toUpperCase()}`;
    case 'COMPARE':
      return `→ comparing ${action.a} / ${action.b}`;
  }
}
